import { useCallback, useEffect, useState } from 'react'
import { Cpu, X, Radio, Loader2 } from 'lucide-react'
import { useAuthStore } from '../../auth/store/authStore'
import { devicesService } from '../../../services/devicesService'
import { propertiesService } from '../../../services/propertiesService'
import { peopleService } from '../../../services/peopleService'
import { teamsService } from '../../../services/teamsService'
import { tablesService } from '../../../services/tablesService'
import { printersService } from '../../../services/printersService'
import type { Device, DeviceLanguage, Property, User, Team, Table, Printer, QRCodeConfigResponse } from '../../../types/api.types'
import { DEVICE_LANGUAGE_OPTIONS, DEFAULT_DEVICE_LANGUAGE, deviceLanguageBadge } from '../../../constants/deviceLanguage'
import { CardOverflowMenu } from './CardOverflowMenu'
import { EmptyState } from './EmptyState'
import { AdminBanner } from './AdminBanner'
import { useSelectedPropertyId, useSelectedPropertyName, useSelectedPropertyType } from '../../../store/propertyStore'

// A tabletop device reports `websocket`. Anything unrecognised stays gray rather
// than being guessed at — a mode badge that lies is worse than one that shrugs.
const MODE_BADGE: Record<string, string> = {
  ai: 'bg-purple-50 text-purple-700',
  udp: 'bg-blue-50 text-blue-700',
  websocket: 'bg-teal-50 text-teal-700',
}
const MODE_CAPTION: Record<string, string> = {
  ai: 'Voice Assistant',
  udp: 'Communication',
  websocket: 'Table orders',
}

// ── DeviceCard ──

function DeviceCard({
  device: d,
  propertyLabel,
  userLabel,
  teamLabel,
  tableLabel,
  printerLabel,
  mode,
  onQuickCommSetup,
  onEdit,
  onDelete,
}: {
  device: Device
  propertyLabel: string
  userLabel?: string
  teamLabel?: string
  tableLabel?: string
  printerLabel?: string
  mode: string
  onQuickCommSetup: () => void
  onEdit: () => void
  onDelete: () => void
}): JSX.Element {
  return (
    <div className="min-w-0 rounded-lg border border-gray-200 bg-white p-5 transition-all duration-200 hover:border-brand-green/50 hover:shadow-md">
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <h3 className="truncate text-[15px] font-semibold text-gray-900">Device {d.id}</h3>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={onQuickCommSetup}
            className="flex items-center justify-center rounded-lg border border-brand-green/40 bg-brand-green/10 p-2 text-brand-green transition-colors hover:bg-brand-green/20"
            title="Set up QuickComm device"
          >
            <Radio size={18} />
          </button>
          <CardOverflowMenu onEdit={onEdit} onDelete={onDelete} />
        </div>
      </div>
      <div className="space-y-2 text-[13px]">
        <div className="flex items-center gap-2">
          <span className="w-20 shrink-0 text-gray-400">Property:</span>
          <span className="min-w-0 flex-1 truncate text-gray-700">{propertyLabel}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-20 shrink-0 text-gray-400">Status:</span>
          <span className={`shrink-0 rounded px-2 py-0.5 text-[11px] font-medium ${
            d.status === 'ACTIVE' ? 'bg-green-50 text-green-700'
              : d.status === 'BLOCKED' ? 'bg-red-50 text-red-700'
              : 'bg-gray-100 text-gray-600'
          }`}>{d.status}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-20 shrink-0 text-gray-400">Mode:</span>
          <span className={`shrink-0 rounded px-2 py-0.5 text-[11px] font-semibold ${
            MODE_BADGE[mode] ?? 'bg-gray-100 text-gray-500'
          }`}>{mode.toUpperCase()}</span>
          {MODE_CAPTION[mode] && (
            <span className="min-w-0 flex-1 truncate text-[11px] text-gray-400">({MODE_CAPTION[mode]})</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="w-20 shrink-0 text-gray-400">Language:</span>
          <span className="shrink-0 rounded bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-700">{deviceLanguageBadge(d.language)}</span>
        </div>
        {userLabel && (
          <div className="flex items-center gap-2">
            <span className="w-20 shrink-0 text-gray-400">User:</span>
            <span className="min-w-0 flex-1 truncate text-gray-700">{userLabel}</span>
          </div>
        )}
        {teamLabel && (
          <div className="flex items-center gap-2">
            <span className="w-20 shrink-0 text-gray-400">Team:</span>
            <span className="min-w-0 flex-1 truncate text-gray-700">{teamLabel}</span>
          </div>
        )}
        {tableLabel && (
          <div className="flex items-center gap-2">
            <span className="w-20 shrink-0 text-gray-400">Table:</span>
            <span className="min-w-0 flex-1 truncate text-gray-700">{tableLabel}</span>
          </div>
        )}
        {printerLabel && (
          <div className="flex items-center gap-2">
            <span className="w-20 shrink-0 text-gray-400">Printer:</span>
            <span className={`min-w-0 flex-1 truncate ${d.printer_id == null ? 'text-amber-700' : 'text-gray-700'}`}>
              {printerLabel}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

// ── DeviceForm ──

interface DeviceFormState {
  propertyId: number | undefined
  userId: number | undefined
  teamId: number | undefined
  tableId: number | undefined
  printerId: number | undefined
  // Deliberately a plain string, not 'ai' | 'udp'. A tabletop device comes back as
  // 'websocket'; narrowing here is what used to silently rewrite it to 'ai' on save.
  communicationMode: string
  language: DeviceLanguage
  deviceName: string
  serverMode: 'cloud' | 'local'
  localIp: string
  localPort: number
}

const EMPTY_FORM: DeviceFormState = {
  propertyId: undefined,
  userId: undefined,
  teamId: undefined,
  tableId: undefined,
  printerId: undefined,
  communicationMode: 'udp',
  language: DEFAULT_DEVICE_LANGUAGE,
  deviceName: 'QuickComm',
  serverMode: 'cloud',
  localIp: '',
  localPort: 8000,
}

// ── DevicesTab ──

interface DevicesTabProps {
  onOpenQuickCommSetup: (data: QRCodeConfigResponse) => void
}

export function DevicesTab({ onOpenQuickCommSetup }: DevicesTabProps): JSX.Element {
  const authUser = useAuthStore((s) => s.user)

  const [devices, setDevices] = useState<Device[]>([])
  const [properties, setProperties] = useState<Property[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [teams, setTeams] = useState<Team[]>([])
  const [tables, setTables] = useState<Table[]>([])
  const [printers, setPrinters] = useState<Printer[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const selectedPropertyId = useSelectedPropertyId()
  const selectedPropertyName = useSelectedPropertyName()
  // /admin sits inside PropertyGuard, so a property is always selected here and
  // every device on screen belongs to it. One type governs the whole tab.
  const isRestaurant = useSelectedPropertyType() === 'RESTAURANT'
  const [filterMode, setFilterMode] = useState<string | undefined>(undefined)

  const [showModal, setShowModal] = useState(false)
  const [editingDeviceId, setEditingDeviceId] = useState<number | null>(null)
  const [form, setForm] = useState<DeviceFormState>(EMPTY_FORM)
  const [formError, setFormError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => { if (success) { const t = setTimeout(() => setSuccess(null), 5000); return () => clearTimeout(t) } }, [success])
  useEffect(() => { if (error) { const t = setTimeout(() => setError(null), 5000); return () => clearTimeout(t) } }, [error])

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [allDevices, allProps, allUsers, allTeams, allTables, allPrinters] = await Promise.all([
        devicesService.getAll(selectedPropertyId ?? undefined, filterMode),
        propertiesService.getAll(),
        peopleService.getAll(),
        teamsService.getAll(),
        // The store hydrates `properties` after first paint, so the type lands a
        // render late. It is a dep, so this re-runs once and picks the tables up.
        isRestaurant && selectedPropertyId ? tablesService.getAll(selectedPropertyId) : Promise.resolve([]),
        isRestaurant && selectedPropertyId ? printersService.getAll(selectedPropertyId) : Promise.resolve([]),
      ])
      setDevices(allDevices)
      setProperties(allProps)
      setUsers(allUsers)
      setTeams(allTeams)
      setTables(allTables)
      setPrinters(allPrinters)
    } catch {
      setError('Failed to load devices')
    } finally {
      setLoading(false)
    }
  }, [selectedPropertyId, filterMode, isRestaurant])

  useEffect(() => { loadData() }, [loadData])

  // A property switch re-keys everything under the modal. Left open, the form would
  // submit a table belonging to the property you just left — a 400 at best.
  useEffect(() => { setShowModal(false) }, [selectedPropertyId])

  function resetForm() {
    setForm(EMPTY_FORM)
    setEditingDeviceId(null)
    setFormError(null)
  }

  function openCreateModal() {
    setForm({ ...EMPTY_FORM, propertyId: selectedPropertyId ?? undefined })
    setEditingDeviceId(null)
    setFormError(null)
    setShowModal(true)
  }

  function openEditModal(device: Device) {
    setForm({
      propertyId: device.property_id,
      userId: device.user_id ?? undefined,
      teamId: device.team_id ?? undefined,
      tableId: device.table_id ?? undefined,
      printerId: device.printer_id ?? undefined,
      // Round-trip the raw mode. Collapsing it to 'ai' | 'udp' here is what turned
      // a saved tabletop device into a voice assistant.
      communicationMode: device.communication_mode ?? 'ai',
      language: device.language ?? DEFAULT_DEVICE_LANGUAGE,
      deviceName: 'QuickComm',
      serverMode: 'cloud',
      localIp: '',
      localPort: 8000,
    })
    setEditingDeviceId(device.id)
    setFormError(null)
    setShowModal(true)
  }

  // The firmware is given an address, not a printer id, so resolve it here — this
  // tab holds the printers list and both QR paths run through it.
  function printerAddressFor(printerId: number | null | undefined) {
    const printer = printers.find(p => p.id === printerId)
    return {
      printer_ip: printer?.host ?? null,
      printer_port: printer?.port ?? null,
    }
  }

  function buildQRData(device: Device | { id: number; mac_address: string }): QRCodeConfigResponse {
    return {
      device_id: device.id,
      mac_address: device.mac_address,
      property_id: form.propertyId!,
      organization_id: authUser?.organization_id ?? 0,
      team_id: form.teamId ?? null,
      user_id: form.userId ?? null,
      // Without this the BLE payload falls back to table 5 and the device prints
      // every order against the wrong table.
      table_id: form.tableId ?? null,
      table_number: tableNumberFor(form.tableId),
      ...printerAddressFor(form.printerId),
    }
  }

  async function handleSubmit() {
    if (!form.propertyId) { setFormError('Please select a property'); return }
    if (isRestaurant) {
      if (!form.tableId) { setFormError('Select the table this device sits on'); return }
      // Optional to the API, fatal in practice: an unassigned device is answered
      // 204 on every print-job poll and never prints. Catch it at the only place
      // anyone would notice, instead of shipping a table that quietly loses orders.
      if (!form.printerId) { setFormError('Pick a printer — a device with none never prints'); return }
    } else if (!form.teamId) {
      setFormError('Please select a team for this device')
      return
    }
    if (form.serverMode === 'local' && !form.localIp.trim()) { setFormError('Server IP is required for local mode'); return }

    setSubmitting(true)
    setFormError(null)

    try {
      if (!editingDeviceId) {
        // CREATE. Two disjoint shapes, written out separately on purpose: the API
        // 400s on the wrong placement field, so the difference should be readable.
        // Restaurants omit communication_mode — the backend defaults a table device
        // to 'websocket', and 'udp' is the only thing this form could send instead.
        const created = await devicesService.create(isRestaurant ? {
          property_id: form.propertyId!,
          table_id: form.tableId!,
          printer_id: form.printerId!,
          language: form.language,
        } : {
          property_id: form.propertyId!,
          user_id: form.userId ?? null,
          team_id: form.teamId!,
          // Backend owns the MAC for both UDP and AI modes.
          mac_address: '',
          communication_mode: form.communicationMode,
          language: form.language,
        })
        setSuccess('Device created successfully')
        setShowModal(false)
        const qrData = buildQRData(created)
        resetForm()
        await loadData()
        onOpenQuickCommSetup(qrData)
      } else {
        // EDIT. The restaurant branch is the re-seat. team_id is omitted, not nulled:
        // PATCH does not enforce the either/or today, and an omitted key survives it
        // starting to. communication_mode stays out for the same reason as create.
        const updated = await devicesService.update(editingDeviceId, isRestaurant ? {
          table_id: form.tableId!,
          printer_id: form.printerId!,
          language: form.language,
        } : {
          property_id: form.propertyId,
          user_id: form.userId ?? null,
          team_id: form.teamId ?? null,
          communication_mode: form.communicationMode,
          language: form.language,
        })
        setSuccess('Device updated successfully')
        setShowModal(false)
        const qrData = buildQRData(updated)
        resetForm()
        await loadData()
        onOpenQuickCommSetup(qrData)
      }
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Failed to save device')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleDelete(id: number) {
    if (!window.confirm('Are you sure you want to delete this device? This action cannot be undone.')) return
    try {
      await devicesService.delete(id)
      setSuccess('Device deleted successfully')
      await loadData()
    } catch {
      setError('Failed to delete device')
    }
  }

  function handleQuickCommSetup(device: Device) {
    const qrData: QRCodeConfigResponse = {
      device_id: device.id,
      mac_address: device.mac_address,
      property_id: device.property_id,
      organization_id: authUser?.organization_id ?? 0,
      team_id: device.team_id ?? null,
      user_id: device.user_id ?? null,
      table_id: device.table_id ?? null,
      table_number: tableNumberFor(device.table_id),
      ...printerAddressFor(device.printer_id),
    }
    onOpenQuickCommSetup(qrData)
  }

  // Filtered teams for modal form
  const filteredTeams = form.propertyId
    ? teams.filter(t => t.property_id === form.propertyId)
    : teams

  // A table holds one device. Occupied tables are not offered — except the one this
  // device already sits on, or the edit form opens on a value with no option.
  const availableTables = tables.filter(t => t.device_id == null || t.device_id === editingDeviceId)

  // Tables only load for a restaurant property, so an unresolved id means "we know a
  // table is set, not which one". Printing `Table {table_id}` would be a confident
  // wrong answer — an id is not a table number.
  function tableNumberFor(tableId: number | null | undefined): number | null {
    if (tableId == null) return null
    return tables.find(t => t.id === tableId)?.table_number ?? null
  }

  function tableLabelFor(d: Device): string | undefined {
    if (d.table_id == null) return undefined
    const number = tableNumberFor(d.table_id)
    return number == null ? 'Assigned' : `Table ${number}`
  }

  // Same rule as tables: an unresolved id means "we know one is set, not which".
  // A missing one is worth saying out loud — that device is not printing.
  function printerLabelFor(d: Device): string | undefined {
    if (!isRestaurant) return undefined
    if (d.printer_id == null) return 'None — never prints'
    return printers.find(p => p.id === d.printer_id)?.name ?? 'Assigned'
  }

  const hasFilters = !!filterMode

  const onlineDevices = devices.filter(d => d.is_online)
  const offlineDevices = devices.filter(d => !d.is_online)

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={24} className="animate-spin text-brand-green" />
        <span className="ml-2 text-sm text-gray-500">Loading devices...</span>
      </div>
    )
  }

  return (
    <div>
      <AdminBanner message={error} variant="error" />
      <AdminBanner message={success} variant="success" />

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm font-semibold text-gray-700">Viewing: {selectedPropertyName ?? 'All Properties'}</p>

          {/* Hidden for restaurants: the only option is udp, and tabletop devices
              are websocket — filtering would read as "all my devices vanished". */}
          {!isRestaurant && (
            <select
              value={filterMode ?? ''}
              onChange={e => setFilterMode(e.target.value || undefined)}
              className="h-9 rounded-lg border border-gray-200 bg-white px-3 text-[13px] text-gray-700 outline-none focus:border-brand-green"
            >
              <option value="">All Modes</option>
              <option value="udp">UDP Mode (Communication)</option>
            </select>
          )}

          {hasFilters && (
            <button
              type="button"
              onClick={() => setFilterMode(undefined)}
              className="text-[12px] text-gray-500 hover:text-gray-700"
            >
              Clear Filters
            </button>
          )}
        </div>

        {/* PropertyGuard passes children through until the first fetch settles, so
            there is a brief window here with no property to create against. */}
        <button
          type="button"
          onClick={openCreateModal}
          disabled={selectedPropertyId == null}
          className="rounded-lg bg-brand-green px-4 py-2 text-[13px] font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          Create Device
        </button>
      </div>

      {/* Grid */}
      {devices.length === 0 ? (
        <EmptyState icon={Cpu} message="No devices found" hint={hasFilters ? 'Try clearing the filters' : undefined} />
      ) : isRestaurant ? (
        // Online/offline is a UDP-hardware concept. Tabletop devices are
        // websocket and their is_online reads as noisy here, so restaurants
        // just get one flat grid.
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {devices.map(d => (
            <DeviceCard
              key={d.id}
              device={d}
              propertyLabel={properties.find(p => p.id === d.property_id)?.name || `Property ${d.property_id}`}
              userLabel={d.user_id ? (users.find(u => u.id === d.user_id)?.name || `User ${d.user_id}`) : undefined}
              teamLabel={d.team_id ? (teams.find(t => t.id === d.team_id)?.name || `Team ${d.team_id}`) : undefined}
              tableLabel={tableLabelFor(d)}
              printerLabel={printerLabelFor(d)}
              mode={d.communication_mode?.toLowerCase() || 'ai'}
              onQuickCommSetup={() => handleQuickCommSetup(d)}
              onEdit={() => openEditModal(d)}
              onDelete={() => handleDelete(d.id)}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-8">
          {([
            { key: 'online', label: 'Online', items: onlineDevices },
            { key: 'offline', label: 'Offline', items: offlineDevices },
          ] as const).map(section => (
            <section key={section.key}>
              <div className="mb-3 flex items-center gap-2">
                <span className={`h-2 w-2 shrink-0 rounded-full ${section.key === 'online' ? 'bg-green-500' : 'bg-gray-300'}`} />
                <h2 className="text-[13px] font-semibold uppercase tracking-wide text-gray-500">{section.label}</h2>
                <span className="text-[12px] text-gray-400">({section.items.length})</span>
              </div>
              {section.items.length === 0 ? (
                <p className="text-[13px] text-gray-400">No {section.label.toLowerCase()} devices</p>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {section.items.map(d => (
                    <DeviceCard
                      key={d.id}
                      device={d}
                      propertyLabel={properties.find(p => p.id === d.property_id)?.name || `Property ${d.property_id}`}
                      userLabel={d.user_id ? (users.find(u => u.id === d.user_id)?.name || `User ${d.user_id}`) : undefined}
                      teamLabel={d.team_id ? (teams.find(t => t.id === d.team_id)?.name || `Team ${d.team_id}`) : undefined}
                      tableLabel={tableLabelFor(d)}
                      printerLabel={printerLabelFor(d)}
                      mode={d.communication_mode?.toLowerCase() || 'ai'}
                      onQuickCommSetup={() => handleQuickCommSetup(d)}
                      onEdit={() => openEditModal(d)}
                      onDelete={() => handleDelete(d.id)}
                    />
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      )}

      {/* Device Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3 sm:p-4">
          <div className="flex max-h-[90dvh] w-full max-w-2xl flex-col rounded-xl bg-white">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-bold text-gray-900">
                {editingDeviceId ? 'Edit Device' : 'Create Device'}
              </h2>
              <button type="button" onClick={() => { setShowModal(false); resetForm() }} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 space-y-5 overflow-y-auto p-4 sm:p-6">
              {formError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-[13px] text-red-600">{formError}</div>
              )}

              {/* Device Assignment */}
              <div>
                <h3 className="mb-3 text-[13px] font-semibold uppercase tracking-wider text-gray-500">Device Assignment</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <span className="mb-1 block text-[13px] font-medium text-gray-700">Property</span>
                    <p className="text-[14px] text-gray-900">{selectedPropertyName ?? '—'}</p>
                  </div>

                  {isRestaurant ? (
                    <>
                      <div>
                        <label htmlFor="device-table" className="mb-1 block text-[13px] font-medium text-gray-700">Table*</label>
                        <select
                          id="device-table"
                          value={form.tableId ?? ''}
                          onChange={e => setForm({ ...form, tableId: Number(e.target.value) })}
                          className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green"
                        >
                          <option value="" disabled>Select table</option>
                          {availableTables.map(t => (
                            <option key={t.id} value={t.id}>Table {t.table_number}</option>
                          ))}
                        </select>
                        {availableTables.length === 0 && (
                          <p className="mt-1 text-[12px] text-gray-400">
                            {tables.length === 0
                              ? 'No tables yet — add one on the Tables tab first.'
                              : 'Every table already has a device.'}
                          </p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="device-printer" className="mb-1 block text-[13px] font-medium text-gray-700">Printer*</label>
                        <select
                          id="device-printer"
                          value={form.printerId ?? ''}
                          onChange={e => setForm({ ...form, printerId: Number(e.target.value) })}
                          className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green"
                        >
                          <option value="" disabled>Select printer</option>
                          {printers.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                        <p className="mt-1 text-[12px] text-gray-400">
                          {printers.length === 0
                            ? 'No printers yet — add one on the Printers tab first.'
                            : 'Where this table’s tickets print.'}
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label htmlFor="device-user" className="mb-1 block text-[13px] font-medium text-gray-700">User</label>
                        <select
                          id="device-user"
                          value={form.userId ?? ''}
                          onChange={e => setForm({ ...form, userId: e.target.value ? Number(e.target.value) : undefined })}
                          className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green"
                        >
                          <option value="">No user assigned</option>
                          {users.map(u => (
                            <option key={u.id} value={u.id}>{u.name}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label htmlFor="device-team" className="mb-1 block text-[13px] font-medium text-gray-700">Team*</label>
                        <select
                          id="device-team"
                          value={form.teamId ?? ''}
                          onChange={e => setForm({ ...form, teamId: Number(e.target.value) })}
                          className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green"
                        >
                          <option value="" disabled>Select team</option>
                          {filteredTeams.map(t => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label htmlFor="device-mode" className="mb-1 block text-[13px] font-medium text-gray-700">Communication Mode*</label>
                        <select
                          id="device-mode"
                          value={form.communicationMode}
                          onChange={e => setForm({ ...form, communicationMode: e.target.value })}
                          className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green"
                        >
                          <option value="udp">UDP - Communication</option>
                        </select>
                      </div>
                    </>
                  )}

                  <div>
                    <label htmlFor="device-language" className="mb-1 block text-[13px] font-medium text-gray-700">Language*</label>
                    <select
                      id="device-language"
                      value={form.language}
                      onChange={e => setForm({ ...form, language: e.target.value as DeviceLanguage })}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green"
                    >
                      {DEVICE_LANGUAGE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Device Settings */}
              <div>
                <h3 className="mb-3 text-[13px] font-semibold uppercase tracking-wider text-gray-500">Device Settings</h3>
                <div>
                  <label className="mb-1 block text-[13px] font-medium text-gray-700">Device Name</label>
                  <input
                    type="text"
                    value={form.deviceName}
                    onChange={e => setForm({ ...form, deviceName: e.target.value })}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green"
                    placeholder="QuickComm"
                  />
                </div>
              </div>

              {/* Server Configuration */}
              <div>
                <h3 className="mb-3 text-[13px] font-semibold uppercase tracking-wider text-gray-500">Server Configuration</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-[13px] font-medium text-gray-700">Server Type*</label>
                    <select
                      value={form.serverMode}
                      onChange={e => setForm({ ...form, serverMode: e.target.value as 'cloud' | 'local' })}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green"
                    >
                      <option value="cloud">Cloud (HTTPS)</option>
                      <option value="local">Local (HTTP)</option>
                    </select>
                  </div>

                  {form.serverMode === 'local' && (
                    <>
                      <div>
                        <label className="mb-1 block text-[13px] font-medium text-gray-700">Server IP Address*</label>
                        <input
                          type="text"
                          value={form.localIp}
                          onChange={e => setForm({ ...form, localIp: e.target.value })}
                          className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green"
                          placeholder="192.168.1.100"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-[13px] font-medium text-gray-700">Server Port*</label>
                        <input
                          type="number"
                          value={form.localPort}
                          onChange={e => setForm({ ...form, localPort: parseInt(e.target.value, 10) || 8000 })}
                          className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-[14px] text-gray-900 outline-none focus:border-brand-green"
                          placeholder="8000"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Fixed footer */}
            <div className="flex flex-col-reverse gap-2 border-t border-gray-200 px-4 py-3 sm:flex-row sm:gap-3 sm:px-6 sm:py-4">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 rounded-lg bg-brand-green py-2.5 text-[14px] font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {submitting ? 'Saving...' : editingDeviceId ? 'Save & open QuickComm setup' : 'Create device & open QuickComm setup'}
              </button>
              <button
                type="button"
                onClick={() => { setShowModal(false); resetForm() }}
                className="rounded-lg border border-gray-300 px-6 py-2.5 text-[14px] font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
